import { ClassroomIcon } from "@components/ClassroomIcon"
import { Card, CardBody } from "@components/ui/Card"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { ArrowRight } from "lucide-react"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { gradeTier, gradeTierBgClass, gradeTierTextClass } from "@/constants/gradeTier"
import type { GradeScale, HomeClassroomChartRow } from "@/types"

interface Props {
  rows: HomeClassroomChartRow[]
  totalClassrooms: number
  scale: GradeScale
  onClassroomClick?: (classroomId: string, source: "chart_bar") => void
}

// Per-classroom average → "group" tier, scale-aware.
const fillColor = (avg: number, scale: GradeScale) =>
  gradeTierBgClass(gradeTier(avg, scale, "group"))
const textColor = (avg: number, scale: GradeScale) =>
  gradeTierTextClass(gradeTier(avg, scale, "group"))

export const HomeAvgByClassroom = memo(function HomeAvgByClassroom({
  rows,
  totalClassrooms,
  scale,
  onClassroomClick,
}: Props) {
  const { t } = useTranslation("home")
  const navigate = useNavigate()
  const scaleMax = scale === "0-100" ? 100 : 10

  const computed = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        widthPct: row.avg_grade > 0 ? Math.min(100, (row.avg_grade / scaleMax) * 100) : 0,
      })),
    [rows, scaleMax],
  )

  const showFooter = totalClassrooms > rows.length

  return (
    <Card>
      <CardBody className="space-y-3">
        <h2 className="text-ink text-sm font-semibold">{t("chart.title")}</h2>
        {/* Visually-hidden a11y table — same data as the chart for screen readers. */}
        <table className="sr-only">
          <caption>{t("chart.aria_caption")}</caption>
          <thead>
            <tr>
              <th>{t("chart.aria_classroom")}</th>
              <th>{t("chart.aria_avg")}</th>
              <th>{t("chart.aria_at_risk")}</th>
            </tr>
          </thead>
          <tbody>
            {computed.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.avg_grade > 0 ? formatGrade(row.avg_grade, scale) : "—"}</td>
                <td>{row.at_risk_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul aria-hidden="true" className="space-y-2">
          {computed.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => {
                  onClassroomClick?.(row.id, "chart_bar")
                  navigate(`/salones/${row.id}/alumnos`)
                }}
                className="hover:bg-inset dark: flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors focus:outline-none focus-visible:ring-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-ink flex items-center gap-1.5 truncate text-sm font-medium">
                    <ClassroomIcon
                      // HomeClassroomChartRow carries only the subject's display
                      // name (see api/home.py::_build_chart), not its default_code
                      // — null falls through to the existing neutral-accent
                      // fallback in accentForSubject.
                      classroom={{ effective_icon: row.icon, subject_default_code: null }}
                      className="h-4 w-4 shrink-0"
                    />
                    {row.name}
                  </p>
                  <p className="text-ink-subtle truncate text-xs">{row.subject}</p>
                  <div className="bg-inset mt-1 h-2 w-full rounded-full">
                    <div
                      className={cn(
                        "h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
                        fillColor(row.avg_grade, scale),
                      )}
                      style={{ width: `${row.widthPct}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      textColor(row.avg_grade, scale),
                    )}
                  >
                    {row.avg_grade > 0 ? formatGrade(row.avg_grade, scale) : t("dash")}
                  </span>
                  {row.at_risk_count > 0 && (
                    <span
                      className="text-danger-ink inline-flex items-center gap-1 text-xs"
                      title={t("chart.at_risk_label", { count: row.at_risk_count })}
                    >
                      <span aria-hidden="true" className="bg-danger h-2 w-2 rounded-full" />
                      {row.at_risk_count}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
        {showFooter && (
          <button
            type="button"
            onClick={() => navigate("/salones")}
            className="text-accent-ink inline-flex items-center gap-1 text-sm font-medium hover:underline"
          >
            {t("chart.view_all")} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </CardBody>
    </Card>
  )
})
